import { useEffect, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";

const Butterfly = ({ size = 30 }) => (
  <motion.div
    animate={{ scaleX: [1, 0.5, 1] }}
    transition={{ duration: 0.34, repeat: Infinity, ease: "easeInOut" }}
    style={{ width: size, height: size, filter: "drop-shadow(0 0 7px hsl(38 95% 60% / 0.55))" }}
  >
    <svg viewBox="0 0 64 64" width="100%" height="100%">
      <defs>
        <linearGradient id="swarm-bfly-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="hsl(46 96% 72%)" />
          <stop offset="100%" stopColor="hsl(30 92% 52%)" />
        </linearGradient>
      </defs>
      <ellipse cx="24" cy="24" rx="13" ry="16" fill="url(#swarm-bfly-gold)" transform="rotate(-22 24 24)" />
      <ellipse cx="40" cy="24" rx="13" ry="16" fill="url(#swarm-bfly-gold)" transform="rotate(22 40 24)" />
      <ellipse cx="26" cy="42" rx="9" ry="12" fill="url(#swarm-bfly-gold)" transform="rotate(-14 26 42)" />
      <ellipse cx="38" cy="42" rx="9" ry="12" fill="url(#swarm-bfly-gold)" transform="rotate(14 38 42)" />
      <rect x="30.8" y="19" width="2.4" height="29" rx="1.2" fill="hsl(28 45% 20%)" />
    </svg>
  </motion.div>
);

// A few golden butterflies that slowly drift across the page, pausing briefly
// mid-flight (as if landing on a word) before drifting off again.
const FLIGHTS = [
  { top: "18%", size: 26, duration: 32, delay: 2, repeatDelay: 11, dir: 1, landX: "42vw", yA: -30, yLand: -6 },
  { top: "46%", size: 32, duration: 40, delay: 9, repeatDelay: 14, dir: 1, landX: "58vw", yA: 28, yLand: 4 },
  { top: "68%", size: 22, duration: 36, delay: 17, repeatDelay: 12, dir: -1, landX: "46vw", yA: -24, yLand: -2 },
  { top: "33%", size: 24, duration: 44, delay: 26, repeatDelay: 16, dir: -1, landX: "60vw", yA: 22, yLand: 6 },
];

export const GoldenButterflies = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden z-[35]" aria-hidden data-testid="chemin-butterfly-swarm">
    {FLIGHTS.map((f, i) => {
      const fromLeft = f.dir === 1;
      const startX = fromLeft ? "-2vw" : "2vw";
      const endX = fromLeft ? "112vw" : "-112vw";
      // 4 keyframes: enter -> arrive at landing spot -> hold (land ~1.5s) -> exit
      const times = [0, 0.42, 0.5, 1];
      return (
        <motion.div
          key={i}
          className="absolute"
          style={{ top: f.top, left: fromLeft ? "-8vw" : "auto", right: fromLeft ? "auto" : "-8vw" }}
          initial={{ x: startX, opacity: 0 }}
          animate={{
            x: [startX, f.landX, f.landX, endX],
            y: [f.yA, f.yLand, f.yLand + 4, f.yA],
            opacity: [0, 0.85, 0.85, 0],
            rotate: fromLeft ? [4, 0, 0, 5] : [-4, 0, 0, -5],
          }}
          transition={{ duration: f.duration, delay: f.delay, repeat: Infinity, repeatDelay: f.repeatDelay, ease: "easeInOut", times }}
        >
          <div style={{ transform: fromLeft ? "none" : "scaleX(-1)" }}>
            <Butterfly size={f.size} />
          </div>
        </motion.div>
      );
    })}
  </div>
);


// A fine puff of golden dust left behind when the butterfly takes off from a word.
const DustTrail = ({ x, y }) => {
  const bits = [...Array(10)].map((_, i) => ({
    i,
    dx: (Math.random() - 0.35) * 46 + 14,   // drift slightly toward the up-right departure
    dy: -(Math.random() * 60) - 6,
    s: 2 + Math.random() * 3,
    delay: i * 0.08,
    dur: 1.2 + Math.random() * 0.9,
  }));
  return (
    <div className="absolute top-0 left-0" style={{ transform: `translate(${x + 12}px, ${y + 12}px)` }}>
      {bits.map((b) => (
        <motion.span
          key={b.i}
          className="absolute rounded-full"
          style={{ width: b.s, height: b.s, background: "hsl(44 96% 68%)", boxShadow: "0 0 6px hsl(40 95% 60% / 0.8)" }}
          initial={{ x: 0, y: 0, opacity: 0.95, scale: 1 }}
          animate={{ x: b.dx, y: b.dy, opacity: 0, scale: 0.2 }}
          transition={{ duration: b.dur, delay: b.delay, ease: "easeOut" }}
        />
      ))}
    </div>
  );
};

// A single butterfly that, from time to time, flies in and lands precisely on a
// word marked with the `.bfly-word` class, pauses ~1.6s, then flutters away
// leaving a fine trail of golden dust.
export const LandingButterfly = () => {
  const controls = useAnimationControls();
  const [takeoff, setTakeoff] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    (async () => {
      await controls.set({ opacity: 0, x: -200, y: 0 });
      await wait(5000);
      while (!cancelled) {
        const targets = Array.from(document.querySelectorAll(".bfly-word"))
          .filter((el) => el.getClientRects().length > 0);
        if (targets.length === 0) { await wait(4000); continue; }
        const el = targets[Math.floor(Math.random() * targets.length)];
        const r = el.getBoundingClientRect();
        const size = 24;
        const tx = r.left + window.scrollX + r.width / 2 - size / 2;
        const ty = r.top + window.scrollY - size * 0.75;
        const fromLeft = Math.random() > 0.5;
        await controls.set({ x: fromLeft ? tx - 180 : tx + 180, y: ty - 90, opacity: 0, rotate: fromLeft ? 5 : -5 });
        if (cancelled) break;
        await controls.start({ x: tx, y: ty, opacity: 0.92, rotate: 0, transition: { duration: 3.2, ease: "easeInOut" } });
        if (cancelled) break;
        await wait(1600); // perched on the word
        if (cancelled) break;
        setTakeoff({ x: tx, y: ty, key: Date.now() }); // emit dust as it lifts off
        await controls.start({ x: tx + 170, y: ty - 190, opacity: 0, rotate: 6, transition: { duration: 3.4, ease: "easeInOut" } });
        if (cancelled) break;
        setTakeoff(null);
        await wait(9000 + Math.random() * 9000);
      }
    })();
    return () => { cancelled = true; };
  }, [controls]);

  return (
    <div className="absolute top-0 left-0 w-full h-0 pointer-events-none z-[36]" aria-hidden data-testid="chemin-landing-butterfly">
      {takeoff && <DustTrail key={takeoff.key} x={takeoff.x} y={takeoff.y} />}
      <motion.div className="absolute top-0 left-0" animate={controls} initial={{ opacity: 0 }}>
        <Butterfly size={24} />
      </motion.div>
    </div>
  );
};
